import React from 'react'
import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
  Svg,
  Path,
} from '@react-pdf/renderer'
import {
  LABEL_SPAN,
  LEDGER_COLUMNS,
  BRAND,
  cellValue,
  fmt,
  isFundAddition,
} from './ledgerConfig'

const C = {
  primary: BRAND.primary,
  tint: BRAND.tint,
  creditTint: BRAND.creditTint,
  text: `#1a1528`,
  muted: `#6b6580`,
  border: `#ddd8e8`,
  zebra: `#faf9fc`,
  green: BRAND.credit,
  red: `#b42318`,
}

const styles = StyleSheet.create({
  page: {
    paddingTop: 28,
    paddingBottom: 40,
    paddingHorizontal: 28,
    fontSize: 8,
    fontFamily: `Helvetica`,
    color: C.text,
  },
  header: {
    flexDirection: `row`,
    justifyContent: `space-between`,
    alignItems: `flex-end`,
    borderBottomWidth: 2,
    borderBottomColor: C.primary,
    paddingBottom: 8,
    marginBottom: 10,
  },
  logo: { width: 128, height: 32 },
  title: {
    fontSize: 13,
    fontFamily: `Helvetica-Bold`,
    color: C.primary,
    marginTop: 14,
  },
  generated: { fontSize: 7.5, color: C.muted, textAlign: `right` },
  filters: {
    flexDirection: `row`,
    flexWrap: `wrap`,
    alignItems: `center`,
    marginBottom: 12,
  },
  filtersTitle: {
    fontSize: 7.5,
    fontFamily: `Helvetica-Bold`,
    color: C.primary,
    letterSpacing: 1,
    marginRight: 8,
    marginBottom: 5,
  },
  chip: {
    flexDirection: `row`,
    alignItems: `center`,
    backgroundColor: C.tint,
    borderLeftWidth: 2.5,
    borderLeftColor: C.primary,
    borderRadius: 3,
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginRight: 6,
    marginBottom: 5,
  },
  chipLabel: {
    fontSize: 6.5,
    color: C.primary,
    opacity: 0.75,
    textTransform: `uppercase`,
    letterSpacing: 0.5,
    marginRight: 4,
  },
  chipValue: { fontSize: 8.5, fontFamily: `Helvetica-Bold`, color: C.text },
  summary: { flexDirection: `row`, marginBottom: 12 },
  card: {
    flex: 1,
    borderWidth: 0.5,
    borderColor: C.border,
    borderRadius: 4,
    padding: 7,
    marginRight: 6,
  },
  cardLast: { marginRight: 0 },
  cardLabel: {
    fontSize: 7,
    color: C.muted,
    marginBottom: 3,
    textTransform: `uppercase`,
  },
  cardValue: { fontSize: 11, fontFamily: `Helvetica-Bold` },
  thead: {
    flexDirection: `row`,
    backgroundColor: C.primary,
    color: `#fff`,
    fontFamily: `Helvetica-Bold`,
    paddingVertical: 5,
    paddingHorizontal: 4,
    borderTopLeftRadius: 3,
    borderTopRightRadius: 3,
  },
  row: {
    flexDirection: `row`,
    paddingVertical: 4,
    paddingHorizontal: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: C.border,
  },
  zebra: { backgroundColor: C.zebra },
  //* fund-addition (credit/IN) transaction rows
  creditRow: { backgroundColor: C.creditTint },
  strongRow: {
    flexDirection: `row`,
    paddingVertical: 5,
    paddingHorizontal: 4,
    backgroundColor: C.tint,
    fontFamily: `Helvetica-Bold`,
  },
  bold: { fontFamily: `Helvetica-Bold` },
  //* every table cell gets the same side padding -> visible gap between columns
  cell: { paddingHorizontal: 4 },
  num: { textAlign: `right` },
  green: { color: C.green },
  red: { color: C.red },
  footer: {
    position: `absolute`,
    bottom: 16,
    left: 28,
    right: 28,
    flexDirection: `row`,
    justifyContent: `space-between`,
    fontSize: 7,
    color: C.muted,
    borderTopWidth: 0.5,
    borderTopColor: C.border,
    paddingTop: 4,
  },
})

const signed = (n) => (Number(n) < 0 ? styles.red : null)

//* built-in Helvetica has no "→" glyph, so draw it as "->" in the PDF
const pdfText = (v) => String(v ?? ``).replace(/→/g, `->`)

//* column style from the shared definition (width + right-aligned amounts)
const colStyle = (c) => [
  styles.cell,
  { width: c.pdfWidth },
  c.numeric && styles.num,
]
const widthOf = (cols) =>
  `${cols.reduce((sum, c) => sum + parseFloat(c.pdfWidth), 0)}%`
const amountCols = LEDGER_COLUMNS.slice(LABEL_SPAN)

//* green for credit, red for debit / negative values
const cellColor = (c, r) => {
  if (c.key === `in`) return styles.green
  if (c.key === `out`) return styles.red
  if (c.key === `balance`) return signed(r?.balance)
  return null
}

const LedgerPdf = ({ ledger, filtersText = ``, logo }) => {
  const generatedAt = new Date().toLocaleString(`en-PK`)
  const chips = filtersText.split(`  |  `).filter(Boolean)

  //* period totals
  const summary = [
    {
      label: `Opening balance`,
      value: `Rs. ${fmt(ledger?.openingBalance)}`,
      style: signed(ledger?.openingBalance),
    },
    {
      label: `Total credit`,
      value: `Rs. ${fmt(ledger?.totals?.in)}`,
      style: styles.green,
    },
    {
      label: `Total debit`,
      value: `Rs. ${fmt(ledger?.totals?.out)}`,
      style: styles.red,
    },
    {
      label: `Closing balance`,
      value: `Rs. ${fmt(ledger?.totals?.closingBalance)}`,
      style: signed(ledger?.totals?.closingBalance),
    },
  ]

  return (
    <Document title="Funds Ledger" author="Brackets Technologies">
      <Page size="A4" orientation="landscape" style={styles.page}>
        <View style={styles.header}>
          <View>
            {logo?.paths?.length ? (
              <Svg viewBox={logo.viewBox} style={styles.logo}>
                {logo.paths.map((path, i) => (
                  <Path
                    key={i}
                    d={path.d}
                    fill={path.fill}
                    fillRule={path.fillRule}
                  />
                ))}
              </Svg>
            ) : (
              <Text style={styles.title}>Brackets</Text>
            )}
            <Text style={styles.title}>Funds Ledger</Text>
          </View>
          <Text style={styles.generated}>Generated on {generatedAt}</Text>
        </View>

        <View style={styles.filters}>
          <Text style={styles.filtersTitle}>FILTERS</Text>
          {chips.map((c) => {
            const [label, ...rest] = c.split(`:`)
            return (
              <View key={c} style={styles.chip}>
                <Text style={styles.chipLabel}>{label}</Text>
                <Text style={styles.chipValue}>{rest.join(`:`).trim()}</Text>
              </View>
            )
          })}
        </View>

        <View style={styles.summary}>
          {summary.map((s, i) => (
            <View
              key={s.label}
              style={[styles.card, i === summary.length - 1 && styles.cardLast]}
            >
              <Text style={styles.cardLabel}>{s.label}</Text>
              <Text style={[styles.cardValue, s.style]}>
                {pdfText(s.value)}
              </Text>
            </View>
          ))}
        </View>

        <View style={styles.thead} fixed>
          {LEDGER_COLUMNS.map((c) => (
            <Text key={c.key} style={colStyle(c)}>
              {pdfText(c.label)}
            </Text>
          ))}
        </View>

        <View style={styles.strongRow}>
          <Text
            style={[
              styles.cell,
              { width: widthOf(LEDGER_COLUMNS.slice(0, -1)) },
            ]}
          >
            Opening balance
          </Text>
          <Text
            style={[
              ...colStyle(LEDGER_COLUMNS[LEDGER_COLUMNS.length - 1]),
              signed(ledger?.openingBalance),
            ]}
          >
            {fmt(ledger?.openingBalance)}
          </Text>
        </View>

        {ledger?.rows?.length === 0 && (
          <View style={styles.row}>
            <Text
              style={{ width: `100%`, textAlign: `center`, color: C.muted }}
            >
              No entries for these filters
            </Text>
          </View>
        )}

        {(ledger?.rows || []).map((r, i) => {
          const addition = isFundAddition(r)
          return (
            <View
              style={[
                styles.row,
                addition ? styles.creditRow : i % 2 === 1 && styles.zebra,
              ]}
              key={r?._id}
              wrap={false}
            >
              {LEDGER_COLUMNS.map((c) => (
                <Text
                  key={c.key}
                  style={[
                    ...colStyle(c),
                    cellColor(c, r),
                    addition && c.key === `in` && styles.bold,
                  ]}
                >
                  {pdfText(cellValue(c, r))}
                </Text>
              ))}
            </View>
          )
        })}

        <View style={styles.strongRow} wrap={false}>
          <Text
            style={[
              styles.cell,
              { width: widthOf(LEDGER_COLUMNS.slice(0, LABEL_SPAN)) },
            ]}
          >
            Totals / Closing balance
          </Text>
          {amountCols.map((c) => {
            const value = {
              in: ledger?.totals?.in,
              out: ledger?.totals?.out,
              balance: ledger?.totals?.closingBalance,
            }[c.key]
            return (
              <Text
                key={c.key}
                style={[...colStyle(c), cellColor(c, { balance: value })]}
              >
                {fmt(value)}
              </Text>
            )
          })}
        </View>

        <View style={styles.footer} fixed>
          <Text>Brackets Technologies | Confidential</Text>
          <Text
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  )
}

export default LedgerPdf
